import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import { Stack, Typography } from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, { AccordionSummaryProps, accordionSummaryClasses } from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useQueryMedia } from "../../../../hooks/useQueryMedia";
import Colors from "../../../../utils/Colors";

const faqData = [
  {
    title: "How is this platform different from other platforms that allow feedback during the lecture?",
    text: `In large classes, asking questions during a lecture can be disruptive for both students and teachers. Similarly, administering quizzes interrupts the flow of teaching and limits the
opportunity for students to ask their own questions. Our platform enables students to give immediate feedback without the need to type or speak, minimizing disruption. This allows teachers to
assess student understanding in real time and adjust their instruction accordingly—without pausing the lecture.`,
  },
  {
    title: "How can the platform increase students’ participation and engagement?",
    text: `In many situations, students may feel uncomfortable raising their hands to admit they don’t understand the material. This hesitation often stems from a fear of peer judgment or a reluctance to
interrupt the lecture. By providing a way for students to express their understanding anonymously, students are incentivized to participate more openly and offer valuable feedback.`,
  },
  {
    title: "What is the best way to use statistics?",
    text: `If possible, a teacher should record the lecture, as the statistics include timestamps for student responses. By recording the lecture, a teacher can align the responses with specific moments
in the presentation, identifying sections that students found engaging or difficult to understand. This allows the teacher to make adjustments or use AI to suggest improvements.`,
  },
  {
    title: "Aren’t there other platforms that also offer anonymous interactions?",
    text: `There are platforms that offer students an option to make posts anonymously, but students are not truly anonymous since either teachers/schools or platforms know students’ identity. Our
platform does not require any personally identifiable information from students when they register, such as emails or phone numbers, so the students’ privacy is completely protected.`,
  },
  {
    title: "Can a teacher be anonymous?",
    text: `A teacher can post a question, provide an answer, and enter discussions under a pseudonym so the teacher can be anonymous during the Q/A/D interactions.`,
  },
  {
    title: "What are the benefits of being anonymous?",
    text: `There are several benefits to anonymity. First, it enables students to ask any question without fearing it might seem "dumb" or too simple to their peers. Second, if a student provides an
incorrect answer, anonymity shields them from the embarrassment of being wrong.`,
  },
  {
    title: "Wouldn’t giving complete anonymity to students result in chaos and offensive behavior?",
    text: `That is possible if anonymity is detached from accountability. However, we developed an algorithm that allows students to be anonymous but keep them accountable for their posts. Therefore, if
a student makes disrespectful or offensive postings, the student’s reputation is lowered, and future postings might not be allowed for some time (or indefinitely).`,
  },
  {
    title: "Isn’t accountable anonymity an oxymoron?",
    text: `It was until we created this platform 😊.`,
  },
  {
    title: "Thanks for making me anonymous but I would like to get recognition for all the work I’ve put into the course.",
    text: `We understand that everyone should be recognized for contributions that they make. Even though you are anonymous, it is possible to share your ‘transcript’ with a teacher (using standard
cryptographic methods) and by providing your transcript we still would not know your identity. The transcript however would not contain information that if revealed would affect (de-anonymize)
other students.`,
  },
  {
    title: "Who are the moderators?",
    text: `Every participant is a moderator. This is a self-moderated platform. There are no external moderators.`,
  },
  {
    title: "Who can see Q/A/Ds?",
    text: `Only the teacher and the students that have registered for a particular course can see the postings made within the lectures of that course.`,
  },
  {
    title: "Can I use my PrivateID that I obtained for one course in another course?",
    text: `Yes. Your PrivateID (and the password) allows you to login to the platform. Once you are in, you can join a lecture from any course by pressing the button “Join Lecture”.`,
  },
  {
    title: "Why do you issue a new pseudonym for every lecture?",
    text: `We chose to assign a new pseudonym for every new lecture in order to better protect students’ privacy - to make it more difficult to figure out who is who.`,
  },
];

export default function HowItWorkFAQ() {
  return (
    <Stack spacing={1} pb={2}>
      {faqData.map((faq, i) => (
        <AccordionFAQ key={i} {...faq} />
      ))}
    </Stack>
  );
}

const accordionBorderRadius = 5;
const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
  borderRadius: accordionBorderRadius + 1,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

interface CustomAccordionSummaryProps extends AccordionSummaryProps {
  expanded: boolean;
}
const AccordionSummary = styled((props: CustomAccordionSummaryProps) => <MuiAccordionSummary expandIcon={<ExpandCircleDownIcon sx={{ fontSize: "1.7rem", color: "white" }} />} {...props} />)(
  ({ theme, expanded }) => ({
    backgroundColor: Colors.MATISSE,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopLeftRadius: accordionBorderRadius,
    borderTopRightRadius: accordionBorderRadius,
    borderBottomLeftRadius: expanded ? 0 : accordionBorderRadius,
    borderBottomRightRadius: expanded ? 0 : accordionBorderRadius,
    [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
      marginLeft: "auto", // Move icon to the far right
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
      transform: "rotate(180deg)",
    },
    [`& .${accordionSummaryClasses.content}`]: {
      marginLeft: theme.spacing(1),
    },
  })
);

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottomLeftRadius: accordionBorderRadius,
  borderBottomRightRadius: accordionBorderRadius,
  backgroundColor: Colors.ANSWER,
}));

function AccordionFAQ({ title, text }: { title: string; text: string }) {
  const [expanded, setExpanded] = React.useState(false);
  const { isMobile } = useQueryMedia();

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expanded={expanded} sx={{ "&.MuiAccordionSummary-root": { minHeight: 40, height: "auto" } }}>
        <Typography variant={isMobile ? "body2" : "body1"}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography fontSize={isMobile ? 12 : 14} sx={{ color: Colors.BLUE_DIANNE }}>
          {text}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
